import React, { useEffect, useRef, useState, useCallback } from 'react';
import moment from 'moment';
import {
  FaMicrophone, FaMicrophoneSlash, FaTimes, FaVolumeUp, FaVolumeMute,
  FaKeyboard, FaRobot, FaArrowLeft, FaExclamationTriangle,
} from 'react-icons/fa';
import { useSpeechRecognition, isSpeechSupported } from './useSpeechRecognition';
import { useTextToSpeech } from './useTextToSpeech';
import { DEFAULT_LANG, nextLang, getLangShortLabel } from './languages';
import { getLocalizedQuestion } from './guidedQuestions';
import { writeText, writeSelect, findByName } from './voiceWrite';
import { matchOption } from './matchOption';
import { GUIDED_STEPS, resolveOptions } from './guidedSteps';
import { matchCommand } from './voiceCommands';
import { playChime } from './chime';
import './GuidedVoiceIntake.css';

const DOB_FORMATS = ['D MMM YYYY', 'DD MMM YYYY', 'D MMMM YYYY', 'DD MMMM YYYY',
  'MMMM D YYYY', 'MMM D YYYY', 'D/M/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'D-M-YYYY'];

/** Seconds to wait before auto-committing a captured, valid answer. */
const ADVANCE_SECONDS = 3;
/** Seconds to wait before auto-skipping an optional step nobody answered. */
const SKIP_SECONDS = 5;

/**
 * decideCountdown — the auto-pilot rule, kept pure so it can be unit tested.
 *
 * A countdown only starts when the outcome is unambiguous. Anything the form
 * would reject (a 7-digit phone number, an unmatched dropdown) parks the tour
 * on the step instead, so the operator sees the existing validation hint and
 * corrects it. Mandatory steps are never auto-skipped.
 *
 * @param {{captured: boolean, stepValid: boolean, optional: boolean}} input
 * @returns {{countdown: number|null, type: 'advance'|'skip'|null}}
 */
export function decideCountdown({ captured, stepValid, optional }) {
  if (!captured) {
    return optional
      ? { countdown: SKIP_SECONDS, type: 'skip' }
      : { countdown: null, type: null };
  }
  if (!stepValid) return { countdown: null, type: null };
  return { countdown: ADVANCE_SECONDS, type: 'advance' };
}

/**
 * Pick the language the question is actually SPOKEN in.
 *
 * Most desktop browsers ship no voice for the Indian languages we offer, and a
 * hands-free tour that goes silent on every step is useless. So when the
 * selected language has no installed voice we speak the English prompt — the
 * localized text stays on screen either way, as visual guidance.
 */
function resolveSpeech(stepId, lang) {
  const voices = typeof window !== 'undefined' && window.speechSynthesis
    ? window.speechSynthesis.getVoices()
    : [];
  const normalize = (l) => String(l).replace('_', '-').toLowerCase();
  const target = normalize(lang);
  const hasVoice = voices.some(
    (v) => normalize(v.lang) === target || normalize(v.lang).startsWith(target.split('-')[0])
  );
  const speakLang = hasVoice ? lang : DEFAULT_LANG;
  return { speakLang, questionText: getLocalizedQuestion(stepId, speakLang) };
}

/** Turn a SpeechRecognition error code into something an operator can act on. */
function describeMicError(err) {
  const code = err && err.error;
  switch (code) {
    case 'no-speech':
    case 'aborted':
      return '';   // routine — the tour already handles silence
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access is blocked. Allow it in your browser’s site settings, or switch to keyboard entry.';
    case 'audio-capture':
      return 'No microphone was found. Plug one in, or switch to keyboard entry.';
    case 'network':
      return 'Speech recognition needs a network connection. Check your connection, or switch to keyboard entry.';
    case 'not-supported':
      return 'Speech recognition isn’t available in this browser. Use keyboard entry instead.';
    default:
      return code ? `Microphone error (${code}). You can still type the answer.` : '';
  }
}

/**
 * GuidedVoiceIntake — step-by-step walkthrough that fills the entire Patient
 * Registration Form. Rendered as an overlay inside the registration modal.
 *
 * The tour runs itself: it speaks each question, opens the mic, and once the
 * answer is captured and valid it commits and moves on after a short countdown.
 * The operator can take over at any point — typing into the preview field stops
 * the mic and the countdown for that step, and a keyboard-only mode turns the
 * mic off entirely while still speaking every question aloud.
 *
 * Every accepted value is committed through the form's OWN handlers (native
 * input/change events for text & selects; onSetDob / onCombo for the date picker
 * and the custom country/state/city combos), so existing validation and state
 * logic run exactly as with manual entry.
 *
 * Props:
 *   show        {boolean}
 *   onClose     {function}
 *   container   {ref}      DOM scope for locating inputs/selects by name
 *   optionData  {object}   live option arrays (gender, blood group, filtered lists…)
 *   onSetDob    {function} (momentDate) => void   — the form's setDOB
 *   onCombo     {function} (comboKind, item|null) => void  — commit a country/state/city pick
 */
export default function GuidedVoiceIntake({ show, onClose, container, optionData, onSetDob, onCombo }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [lang, setLang] = useState(DEFAULT_LANG);
  const [textValue, setTextValue] = useState('');
  const [selectValue, setSelectValue] = useState('');
  const [isMuted, setIsMuted] = useState(false);

  // Auto-pilot: countdowns that commit/skip on their own. Off = the original
  // click-through-every-step behaviour, as an escape hatch when recognition is
  // behaving badly at a particular desk.
  const [autoPilot, setAutoPilot] = useState(true);
  // Keyboard-only: never open the mic. Questions are still spoken aloud.
  const [keyboardMode, setKeyboardMode] = useState(!isSpeechSupported);
  // The operator has taken this step over by typing — no mic, no countdown.
  const [manualEdit, setManualEdit] = useState(false);
  // Set when arriving from Back, so a revisited step doesn't immediately
  // re-commit the value it was seeded with and bounce forward again.
  const [autoSuppressed, setAutoSuppressed] = useState(false);

  const [countdown, setCountdown] = useState(null);
  const [countdownType, setCountdownType] = useState(null);   // 'advance' | 'skip'
  const [micError, setMicError] = useState('');
  // The mic has opened and closed again for this step, so the answer (or the
  // silence) is final. Without this the tour would judge the step during the
  // gap between TTS ending and the mic opening, when nothing has been heard
  // yet — flashing a skip countdown at the operator before they can speak.
  const [listenDone, setListenDone] = useState(false);

  const dobMomentRef = useRef(null);
  const prevStepIndexRef = useRef(0);
  const manualEditRef = useRef(false);
  const keyboardModeRef = useRef(keyboardMode);
  const previewRef = useRef(null);
  const wasListeningRef = useRef(false);
  // Handlers are written here every render so effects and the speech callback
  // can invoke the CURRENT ones without being re-created (and without the stale
  // closures that reading render-scope values inside those effects would cause).
  const actionsRef = useRef({});

  useEffect(() => { manualEditRef.current = manualEdit; }, [manualEdit]);
  useEffect(() => { keyboardModeRef.current = keyboardMode; }, [keyboardMode]);

  const { speak, cancel, isSpeaking } = useTextToSpeech();

  const step = GUIDED_STEPS[stepIndex];
  const isSelect = step && (step.kind === 'select' || step.kind === 'combo');
  const resolved = isSelect ? resolveOptions(step, optionData) : null;

  const rows = resolved
    ? resolved.options.map((o) => ({
        key: String(step.kind === 'combo' ? o.id : resolved.getValue(o)),
        label: resolved.getLabel(o),
        item: o,
        value: resolved.getValue(o),
      }))
    : [];

  const handleResult = useCallback((text) => {
    if (!step) return;

    // Hands-free navigation words are checked first, before any parsing, so
    // "skip" moves on rather than being recorded as an answer.
    const command = matchCommand(text);
    if (command) {
      const actions = actionsRef.current;
      if (command === 'accept') {
        if (actions.canAccept) actions.accept();
      } else if (command === 'redo') {
        actions.redo();
      } else if (command === 'skip') {
        if (step.optional) actions.skip();
      } else if (command === 'back') {
        actions.back();
      } else if (command === 'exit') {
        actions.exit();
      }
      return;
    }

    // The operator is typing this step — don't let stray speech overwrite it.
    if (manualEditRef.current) return;

    setAutoSuppressed(false);

    if (step.kind === 'text' || step.kind === 'number') {
      setTextValue(step.parser ? step.parser(text) : text);
    } else if (step.kind === 'dob') {
      const m = moment(text, DOB_FORMATS, true);
      const parsed = m.isValid() ? m : moment(text);
      if (parsed.isValid()) {
        dobMomentRef.current = parsed;
        setTextValue(parsed.format('YYYY-MM-DD'));
      } else {
        setTextValue('');
      }
    } else {
      // select / combo — smart match against the live options
      const opts = resolved ? resolved.options : [];
      const getVal = step.kind === 'combo' ? (o) => o.id : resolved.getValue;
      const m = matchOption(text, opts, { getLabel: resolved.getLabel, getValue: getVal });
      if (m) setSelectValue(String(m.value));
    }
  }, [step, resolved]);

  const handleMicError = useCallback((e) => {
    const message = describeMicError(e);
    if (!message) return;
    setMicError(message);
    setCountdown(null);
    setCountdownType(null);
  }, []);

  const { start, stop, isListening, interim } = useSpeechRecognition({
    lang,
    long: !!(step && step.long),
    onResult: handleResult,
    onError: handleMicError,
  });

  // Speak the current question, then open the mic once speech ends.
  //
  // Muting skips the audio; keyboard mode skips the mic. They are independent:
  // in keyboard mode the question is still read aloud, because being told what
  // to enter is the whole point of the guided flow.
  useEffect(() => {
    if (!show || !step) return undefined;

    const { speakLang, questionText } = resolveSpeech(step.id, lang);

    stop(); // never listen while speaking

    const openMic = () => {
      if (!keyboardModeRef.current && !manualEditRef.current) start();
    };

    if (isMuted) {
      const startTimer = setTimeout(openMic, 300);
      return () => { clearTimeout(startTimer); stop(); };
    }

    const speakTimer = setTimeout(() => {
      speak(questionText, speakLang, openMic);
    }, 150);
    return () => { clearTimeout(speakTimer); cancel(); stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, stepIndex, lang, isMuted, keyboardMode]);

  // Reset the per-step draft whenever the step changes. Moving BACKWARD seeds
  // the draft from whatever is already in the form, so a revisited step shows
  // the stored value instead of looking empty. Moving forward never seeds —
  // a field carrying a default would otherwise auto-commit before the operator
  // had a chance to answer.
  useEffect(() => {
    const goingBack = stepIndex < prevStepIndexRef.current;
    prevStepIndexRef.current = stepIndex;

    setCountdown(null);
    setCountdownType(null);
    setMicError('');
    setManualEdit(keyboardModeRef.current);
    setAutoSuppressed(goingBack);
    setListenDone(false);
    wasListeningRef.current = false;
    dobMomentRef.current = null;

    const current = GUIDED_STEPS[stepIndex];
    let seedText = '';
    let seedSelect = '';

    // dob and the country/state/city combos have no readable [name] value —
    // the antd picker and the custom combos don't expose one — so they start
    // blank and are simply re-entered.
    if (goingBack && current) {
      const el = findByName(container, current.name);
      if (el) {
        if (current.kind === 'select' && el.tagName === 'SELECT') {
          seedSelect = el.value || '';
        } else if ((current.kind === 'text' || current.kind === 'number')
          && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
          seedText = el.value || '';
        }
      }
    }

    setTextValue(seedText);
    setSelectValue(seedSelect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  // Restart at the first step each time the overlay is opened. The ref is reset
  // first so the step-change effect below sees a forward move, not a Back.
  useEffect(() => {
    if (!show) return;
    prevStepIndexRef.current = 0;
    setStepIndex(0);
    setTextValue('');
    setSelectValue('');
    dobMomentRef.current = null;
    setMicError('');
    setAutoSuppressed(false);
    setCountdown(null);
    setCountdownType(null);
    setListenDone(false);
    wasListeningRef.current = false;
  }, [show]);

  // Switching to keyboard entry hands the current step over immediately.
  useEffect(() => {
    if (keyboardMode) {
      stop();
      setManualEdit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardMode]);

  // Audible cue that the mic is now open, for an operator who isn't watching.
  useEffect(() => {
    if (show && isListening) playChime('start');
  }, [show, isListening]);

  // Watch for the mic closing again — that's the moment the answer is final.
  useEffect(() => {
    if (isListening) {
      wasListeningRef.current = true;
    } else if (wasListeningRef.current) {
      wasListeningRef.current = false;
      setListenDone(true);
    }
  }, [isListening]);

  const digitCount = textValue.replace(/\D/g, '').length;
  const captured = textValue.trim() !== '' || selectValue !== '';
  const stepValid = !step
    ? false
    : isSelect
      ? selectValue !== ''
      : step.minDigits
        ? digitCount === step.minDigits
        : textValue.trim() !== '';
  // A mandatory step (no `optional` flag) must be filled and valid before Accept.
  const acceptDisabled = !step || (!step.optional && !stepValid);

  // Decide whether the tour should advance, skip, or wait. Runs only once the
  // question has finished being spoken and the mic has closed.
  useEffect(() => {
    if (!show || !step || !autoPilot) return;
    if (isSpeaking) return;
    if (manualEdit || keyboardMode || autoSuppressed || micError) {
      setCountdown(null);
      setCountdownType(null);
      return;
    }
    // Still speaking, still listening, or the mic never ran (a permission error,
    // say) — nothing to judge yet.
    if (isListening || !listenDone) {
      setCountdown(null);
      setCountdownType(null);
      return;
    }

    const next = decideCountdown({ captured, stepValid, optional: !!step.optional });
    setCountdown(next.countdown);
    setCountdownType(next.type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, stepIndex, isListening, listenDone, isSpeaking, autoPilot, manualEdit,
    keyboardMode, autoSuppressed, micError, captured, stepValid]);

  // Countdown ticker — at zero, hand off to the accept/skip action.
  useEffect(() => {
    if (countdown === null || !show || !autoPilot) return undefined;

    if (countdown <= 0) {
      if (countdownType === 'advance') actionsRef.current.accept?.();
      else if (countdownType === 'skip') actionsRef.current.skip?.();
      return undefined;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => clearTimeout(timer);
  }, [countdown, countdownType, show, autoPilot]);

  // Keyboard shortcuts. The overlay covers the form beneath it, so a document
  // listener can't reach the underlying inputs.
  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      const actions = actionsRef.current;
      if (e.key === 'Escape') {
        // Capture phase + stopPropagation: otherwise the surrounding
        // react-bootstrap Modal also sees Escape and closes the whole
        // registration dialog along with the overlay.
        e.preventDefault();
        e.stopPropagation();
        actions.exit?.();
        return;
      }
      if (e.key === 'ArrowLeft' && e.altKey) {
        e.preventDefault();
        actions.back?.();
        return;
      }
      if (e.key === 'Enter') {
        // On the address steps Enter is a newline, not a commit.
        if (e.target && e.target.tagName === 'TEXTAREA') return;
        if (actions.canAccept) {
          e.preventDefault();
          actions.accept?.();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [show]);

  // Put the cursor where the operator is about to type.
  useEffect(() => {
    if (show && keyboardMode && previewRef.current) previewRef.current.focus();
  }, [show, keyboardMode, stepIndex]);

  if (!show || !step) return null;

  const showLengthHint = !!step.minDigits && textValue.trim() !== '' && digitCount !== step.minDigits;

  // Localized spoken-prompt text, shown on screen when a non-English language is
  // selected so the operator can read it even when no TTS voice is available.
  const localizedPrompt = getLocalizedQuestion(step.id, lang);
  const showLocalizedPrompt = lang !== DEFAULT_LANG;
  const previousStep = stepIndex > 0 ? GUIDED_STEPS[stepIndex - 1] : null;

  const goNext = () => {
    cancel();
    stop();
    setCountdown(null);
    setCountdownType(null);
    if (stepIndex < GUIDED_STEPS.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      onClose();
    }
  };

  const goBack = () => {
    if (stepIndex === 0) return;
    cancel();
    stop();
    setCountdown(null);
    setCountdownType(null);
    setStepIndex((i) => i - 1);
  };

  const commit = () => {
    if (step.kind === 'text' || step.kind === 'number') {
      writeText(findByName(container, step.name), textValue);
    } else if (step.kind === 'dob') {
      const m = dobMomentRef.current || moment(textValue, 'YYYY-MM-DD', true);
      if (m && m.isValid()) onSetDob(m);
    } else if (step.kind === 'select') {
      writeSelect(findByName(container, step.name), selectValue);
    } else if (step.kind === 'combo') {
      const row = rows.find((r) => r.key === selectValue);
      onCombo(step.comboKind, row ? row.item : null);
    }
    playChime('success');
    goNext();
  };

  const skip = () => {
    playChime('skip');
    goNext();
  };

  const handleClose = () => {
    cancel();
    stop();
    onClose();
  };

  const restart = () => {
    cancel();
    setTextValue('');
    setSelectValue('');
    dobMomentRef.current = null;
    setCountdown(null);
    setCountdownType(null);
    setAutoSuppressed(false);
    setMicError('');
    setListenDone(false);
    wasListeningRef.current = false;
    // Redo hands the step back to the mic — unless the operator has chosen to
    // type everything, in which case it just clears the field.
    if (!keyboardMode) {
      setManualEdit(false);
      start();
    }
  };

  /** Hand the step back to the mic, keeping whatever has already been captured. */
  const resumeMic = () => {
    setManualEdit(false);
    setAutoSuppressed(false);
    setMicError('');
    setListenDone(false);
    wasListeningRef.current = false;
    cancel();
    start();
  };

  /**
   * Re-read the current question. The mic is closed first — otherwise the
   * browser transcribes our own speech straight into the field — and reopened
   * afterwards only if it was already listening.
   */
  const replayQuestion = () => {
    const { speakLang, questionText } = resolveSpeech(step.id, lang);
    const wasListening = isListening;
    cancel();
    stop();
    speak(questionText, speakLang, () => {
      if (wasListening && !keyboardModeRef.current && !manualEditRef.current) start();
    });
  };

  // The operator touched the preview field: this step is theirs now.
  const takeOverByTyping = () => {
    setCountdown(null);
    setCountdownType(null);
    if (!manualEditRef.current) {
      setManualEdit(true);
      stop();
    }
  };

  const handleTextEdit = (value) => {
    takeOverByTyping();
    setTextValue(value);
  };

  actionsRef.current = {
    accept: commit,
    skip,
    redo: restart,
    back: goBack,
    exit: handleClose,
    canAccept: !acceptDisabled,
  };

  const editHandlers = {
    onFocus: takeOverByTyping,
    onClick: takeOverByTyping,
  };

  const statusText = isSpeaking
    ? 'Speaking question…'
    : isListening
      ? (interim || 'Listening…')
      : manualEdit
        ? 'Typing — mic paused'
        : 'Mic idle — click to speak';

  return (
    <div className="gvi-overlay" role="dialog" aria-label="Guided voice registration">
      <div className="gvi-card">
        <div className="gvi-header">
          <span className="gvi-step-count">Step {stepIndex + 1} of {GUIDED_STEPS.length}</span>
          <div className="gvi-header-right">
            <button
              type="button"
              className={`gvi-toggle ${autoPilot ? 'on' : ''}`}
              onClick={() => setAutoPilot((a) => !a)}
              title={autoPilot
                ? 'Auto-advance is on — turn off to confirm every step yourself'
                : 'Auto-advance is off — turn on to move through steps automatically'}
              aria-pressed={autoPilot}
            >
              <FaRobot />
            </button>
            <button
              type="button"
              className={`gvi-toggle ${keyboardMode ? 'on' : ''}`}
              onClick={() => setKeyboardMode((k) => !k)}
              disabled={!isSpeechSupported}
              title={!isSpeechSupported
                ? 'This browser has no speech recognition — keyboard entry only'
                : keyboardMode
                  ? 'Keyboard entry — questions are still spoken. Click to use the mic'
                  : 'Switch to keyboard entry (questions stay spoken)'}
              aria-pressed={keyboardMode}
            >
              <FaKeyboard />
            </button>
            <button
              type="button"
              className={`gvi-mute ${isMuted ? 'muted' : ''}`}
              onClick={() => setIsMuted((m) => !m)}
              title={isMuted ? 'Unmute spoken questions' : 'Mute spoken questions'}
              aria-label={isMuted ? 'Unmute spoken questions' : 'Mute spoken questions'}
            >
              {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
            </button>
            <button
              type="button"
              className="gvi-lang"
              onClick={() => setLang((c) => nextLang(c))}
              title="Change dictation language"
            >
              {getLangShortLabel(lang)}
            </button>
            <button type="button" className="gvi-close" onClick={handleClose} aria-label="Exit guided mode">
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="gvi-progress">
          <div className="gvi-progress-bar" style={{ width: `${((stepIndex + 1) / GUIDED_STEPS.length) * 100}%` }} />
        </div>

        <div className="gvi-question">
          What is the <b>{step.question}</b>?
          {step.optional
            ? <span className="gvi-optional"> (optional)</span>
            : <span className="gvi-required" title="Mandatory field">*</span>}
          <button
            type="button"
            className="gvi-replay"
            onClick={replayQuestion}
            title="Read the question again"
            aria-label="Read the question again"
          >
            <FaVolumeUp />
          </button>
        </div>
        {showLocalizedPrompt && (
          <div className="gvi-question-localized" lang={lang}>{localizedPrompt}</div>
        )}
        {step.example && <div className="gvi-example">Try: <code>{step.example}</code></div>}

        <div className={`gvi-status ${isSpeaking ? 'speaking' : isListening ? 'listening' : ''}`}>
          <button
            type="button"
            className={`gvi-mic ${isListening ? 'active' : ''}`}
            onClick={isListening ? stop : resumeMic}
            disabled={isSpeaking || keyboardMode}
            aria-label={isListening ? 'Stop listening' : 'Start listening'}
          >
            {isListening ? <FaMicrophoneSlash /> : <FaMicrophone />}
          </button>
          <span className="gvi-status-text">{statusText}</span>
          {isListening && (
            <span className="gvi-soundwave" aria-hidden="true">
              <span className="gvi-soundwave-bar" />
              <span className="gvi-soundwave-bar" />
              <span className="gvi-soundwave-bar" />
              <span className="gvi-soundwave-bar" />
              <span className="gvi-soundwave-bar" />
            </span>
          )}
          {countdown !== null && (
            <span className={`gvi-countdown-badge ${countdownType}`}>
              {countdownType === 'advance'
                ? `Advancing in ${countdown}s`
                : `Skipping in ${countdown}s`}
            </span>
          )}
        </div>

        <div className="gvi-preview">
          <label className="gvi-preview-label">Understood (you can edit):</label>
          {isSelect ? (
            <select
              ref={previewRef}
              className="gvi-input"
              value={selectValue}
              onChange={(e) => { takeOverByTyping(); setSelectValue(e.target.value); }}
              {...editHandlers}
            >
              <option value="">- Select -</option>
              {rows.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          ) : step.kind === 'dob' ? (
            <input
              ref={previewRef}
              type="date"
              className="gvi-input"
              value={textValue}
              onChange={(e) => {
                takeOverByTyping();
                setTextValue(e.target.value);
                dobMomentRef.current = e.target.value ? moment(e.target.value, 'YYYY-MM-DD') : null;
              }}
              {...editHandlers}
            />
          ) : step.long ? (
            <textarea
              ref={previewRef}
              className="gvi-input"
              rows={2}
              value={textValue}
              onChange={(e) => handleTextEdit(e.target.value)}
              placeholder="Type or speak to edit…"
              {...editHandlers}
            />
          ) : (
            <input
              ref={previewRef}
              type="text"
              className="gvi-input"
              value={textValue}
              onChange={(e) => handleTextEdit(e.target.value)}
              placeholder="Type or speak to edit…"
              {...editHandlers}
            />
          )}
        </div>

        {micError && (
          <div className="gvi-warning-msg">
            <FaExclamationTriangle /> {micError}
          </div>
        )}
        {showLengthHint && (
          <div className="gvi-hint">Please enter exactly {step.minDigits} digits.</div>
        )}
        {!step.optional && !stepValid && !showLengthHint && (
          <div className="gvi-hint">This field is mandatory — it can’t be skipped.</div>
        )}

        <div className="gvi-actions">
          <button
            type="button"
            className="gvi-btn"
            onClick={goBack}
            disabled={stepIndex === 0}
            title={previousStep ? `Back to ${previousStep.label}` : 'This is the first step'}
          >
            <FaArrowLeft /> Back
          </button>
          <button type="button" className="gvi-btn primary" onClick={commit} disabled={acceptDisabled}>
            {stepIndex === GUIDED_STEPS.length - 1 ? 'Accept & Finish' : 'Accept & Next'}
          </button>
          <button type="button" className="gvi-btn" onClick={restart}>Redo</button>
          {step.optional && (
            <button type="button" className="gvi-btn ghost" onClick={skip}>Skip</button>
          )}
        </div>

        <div className="gvi-footer">
          {previousStep
            ? <span className="gvi-prev-info">Back goes to <strong>{previousStep.label}</strong></span>
            : <span className="gvi-prev-info">First step</span>}
          <span className="gvi-shortcuts">Enter = next · Alt+← = back · Esc = exit</span>
        </div>
      </div>
    </div>
  );
}

export { isSpeechSupported };
